import { newLocalId } from '@/lib/protocol-ids';
import type { GuardrailActionKind, RuleSettingsDraftContent } from '@matter_extractor/shared';
import {
  GUARDRAIL_CONSECUTIVE_TURNS_DEFAULT,
  GUARDRAIL_CONSECUTIVE_TURNS_MAX,
} from '@matter_extractor/shared';

/**
 * What Réglages > Règles edits while somebody types (§8.5, §11.3).
 *
 * Pure, so it can be tested without a base and without a render, and so the
 * route stays the thin wiring `vitest.config.ts` deliberately leaves out of the
 * coverage report - the régime `lib/persona-settings.ts` sets next door.
 *
 * ## One draft, two lists, since issue #192
 *
 * The rules and the guardrails are ONE versioned object (« Les règles », vitrine.md
 * section 5): the rows below are the rows of ITS draft, and everything that was
 * a write of their own - the block of the rules, the save of one guardrail, the
 * « dirty » of a section - is the layer's now (the autosave, the comparison that
 * greys « Enregistrer »). What stays here is what only these lists know: the
 * rows themselves, the new ones, and the things an operator gets wrong by
 * accident - an empty field, a run of turns that is not a number, a direction to
 * inject that nobody wrote - said BEFORE a round trip, in a sentence, so a
 * greyed « Enregistrer une nouvelle version » is never a button an operator
 * clicks three times before reading the form.
 *
 * The server stays the authority on every rule: `ruleSettingsContentSchema` is
 * what a version is checked against, and what the head's button asks too.
 */

/** One editable rule: its identifier, which the version keeps, and its text. */
export type RuleRow = RuleSettingsDraftContent['rules'][number];

/**
 * One editable guardrail, as the DRAFT of a version holds it - the action a
 * union, `consecutiveTurns` a number. The text of an « injecter une direction »
 * that the operator then switched away from is kept by the list for the life of
 * the page (`GuardrailList`), where the draft - which has no place for it on
 * another action - cannot.
 */
export type GuardrailRow = RuleSettingsDraftContent['guardrails'][number];

/* --------------------------------------------------------- the global rules */

/**
 * An empty rule, appended by « Ajouter une règle ». Its identifier is struck
 * here, once: a rule keeps it across versions, so what a turn recorded about it
 * still names it, and it is the React key of a list whose rows move.
 */
export function newRuleRow(): RuleRow {
  return { id: newLocalId(), text: '' };
}

/**
 * Why the rules cannot become a version, in French, or `null` when they can.
 *
 * A sentence rather than a boolean: a button that greys itself without saying
 * why is a button an operator clicks three times before reading the form.
 *
 * No minimum, where the involvement scale keeps two: §11.3 lets the team remove
 * every rule, and `rulesDirective` renders nothing at all on an empty list.
 */
export function rulesBlocked(rows: readonly RuleRow[]): string | null {
  return rows.some((row) => row.text.trim() === '')
    ? 'Chaque règle porte une direction. Une ligne vide ne dit rien à l’interviewer.'
    : null;
}

/* ----------------------------------------------------------- the guardrails */

/** An empty guardrail, appended by « Ajouter un garde-fou ». */
export function newGuardrailRow(): GuardrailRow {
  return {
    id: newLocalId(),
    detection: '',
    consecutiveTurns: GUARDRAIL_CONSECUTIVE_TURNS_DEFAULT,
    // « fermer l'expérience » is the action §11.3's own first guardrail takes,
    // and the one an operator most often means. It is a default, not an
    // opinion: the select is right there.
    action: { kind: 'close-experience' },
  };
}

/**
 * The action of a kind, carrying the direction only when the kind has one.
 *
 * The draft's action is the union of §8.5, where only `inject-direction` holds a
 * text; the list remembers the text typed there so that an operator who picks
 * « injecter une direction », types four lines, then switches to « signaler
 * seulement » and back finds their four lines again.
 */
export function actionOfKind(kind: GuardrailActionKind, direction: string): GuardrailRow['action'] {
  return kind === 'inject-direction' ? { kind, text: direction } : { kind };
}

/** The direction a row carries, or the empty string on an action that has none. */
export function directionOf(row: GuardrailRow): string {
  return row.action.kind === 'inject-direction' ? row.action.text : '';
}

/**
 * Why one guardrail cannot become a version, in French, or `null` when it can.
 *
 * The three things the server refuses with a 400 and that an operator meets by
 * accident: a field left empty, a run of turns that is not a whole number in
 * range, and « injecter une direction » with no direction - the one the union
 * of §8.5 exists to make impossible, and the one a form can walk into by
 * switching a select.
 */
export function guardrailBlocked(row: GuardrailRow): string | null {
  if (row.detection.trim() === '') {
    return 'Un garde-fou dit ce que le scribe doit détecter sur le tour.';
  }
  if (
    !Number.isInteger(row.consecutiveTurns) ||
    row.consecutiveTurns < 1 ||
    row.consecutiveTurns > GUARDRAIL_CONSECUTIVE_TURNS_MAX
  ) {
    return `Le nombre de tours consécutifs est un entier, de 1 à ${GUARDRAIL_CONSECUTIVE_TURNS_MAX}.`;
  }
  if (row.action.kind === 'inject-direction' && row.action.text.trim() === '') {
    return 'Cette action injecte une direction : écrivez-la.';
  }
  return null;
}

/**
 * Whether a TYPED run of turns is a value the draft can hold: a whole number
 * from 1 to the bound. The field keeps what was typed and the draft only moves
 * on a value that parses - the régime of the euro fields of the models.
 */
export function parseConsecutiveTurns(typed: string): number | null {
  if (typed.trim() === '') return null;
  const turns = Number(typed);
  return Number.isInteger(turns) && turns >= 1 && turns <= GUARDRAIL_CONSECUTIVE_TURNS_MAX
    ? turns
    : null;
}
